import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import DynamicLocationField from "@/components/Common/forms/DynamicLocationField";
import InputField from "@/components/Common/forms/InputField";
import CountrySelect from "@/components/ui/CountrySelect";
import useCountries from "@/hooks/useCountries";
import clsxm from "@/lib/clsxm";
import {debounce} from "lodash";
import {memo, useCallback, useState} from "react";
import {useTranslation} from "react-i18next";

type AddressFormProps<T> = {
  cityKey: keyof T;
  countryKey: keyof T;
  errors?: Record<string, string>;
  inputClassName?: string;
  isDisabled?: boolean;
  onDynamicAddressBlur?: () => void;
  onDynamicAddressFocus?: () => void;
  placeDescriptionKey: keyof T;
  placeIdKey: keyof T;
  postalKey: keyof T;
  storeForm: T;
  storeSetter: (key: any, value: T[keyof T]) => void;
  streetKey: keyof T;
  title?: string;
  validateForm?: () => boolean;
};

const AddressForm = ({
  cityKey,
  countryKey,
  errors,
  inputClassName,
  isDisabled,
  onDynamicAddressBlur,
  onDynamicAddressFocus,
  placeDescriptionKey,
  placeIdKey,
  postalKey,
  storeForm,
  storeSetter,
  streetKey,
  title,
  validateForm,
}: AddressFormProps<Record<string, any>>) => {
  const {t} = useTranslation();
  const {getCountryCodeByEnLabel, getCountryEnLabelByTranslatedLabel, getCountryEnLabelByCode, allCountries} =
    useCountries();
  const [areManualAddressFieldsShown, setManualAddressFieldsShown] = useState(
    !storeForm[placeDescriptionKey] && !!storeForm[cityKey],
  );
  const [isSwitchButtonShown, setSwitchButtonShown] = useState(areManualAddressFieldsShown);

  const onChangeManualField = useCallback(
    (key: string, value: string) => {
      storeSetter(key, value);

      storeSetter(placeIdKey, null);
      storeSetter(placeDescriptionKey, null);
    },
    [storeSetter, placeIdKey, placeDescriptionKey],
  );

  if (!allCountries) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between">
        <label className="text-neutral-grey-dark text-sm font-medium">
          {t(areManualAddressFieldsShown ? "Country" : (title ?? "companiesAddress"))}
        </label>
        {isSwitchButtonShown && (
          <button
            className="text-primary-dark-solid flex items-center gap-2"
            onClick={() => setManualAddressFieldsShown(!areManualAddressFieldsShown)}>
            <span className="items-center text-xs">
              {t(`company_profile:enable_${areManualAddressFieldsShown ? "autocomplete" : "manual"}_address`)}
            </span>
            <RightArrowIcon className="size-3" />
          </button>
        )}
      </div>
      {areManualAddressFieldsShown ? (
        <div className="flex flex-col gap-2">
          <CountrySelect
            value={getCountryCodeByEnLabel(storeForm[countryKey])}
            onChange={country => onChangeManualField(countryKey, getCountryEnLabelByCode(country))}
            hidePhoneNumber
            showCountryName
            buttonClassName={clsxm("w-full border rounded-md justify-start gap-2", inputClassName)}
            chevronClassName="ml-auto mr-2"
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <InputField
              placeholder={t("companiesAddressPlaceholder")}
              onChange={value => onChangeManualField(streetKey, value)}
              className={clsxm(
                "border-primary-light-solid",
                inputClassName,
                errors && errors[streetKey] && "border-danger-medium-red focus:border-danger-dark-red",
              )}
              error={errors ? errors[streetKey] : null}
              value={storeForm[streetKey] ?? ""}
              isDisabled={isDisabled}
              label={t("companiesAddressPlaceholder")}
              onBlur={validateForm}
            />
            <InputField
              placeholder={t("city")}
              onChange={value => onChangeManualField(cityKey, value)}
              className={clsxm(
                "border-primary-light-solid",
                inputClassName,
                errors && errors[cityKey] && "border-danger-medium-red focus:border-danger-dark-red",
              )}
              error={errors ? errors[cityKey] : null}
              value={storeForm[cityKey] ?? ""}
              label={t("city")}
              isDisabled={isDisabled}
              onBlur={validateForm}
            />
          </div>
          <InputField
            placeholder={t("postal_code")}
            onChange={value => onChangeManualField(postalKey, value)}
            className={clsxm(
              "border-primary-light-solid sm:w-1/2",
              inputClassName,
              errors && errors[postalKey] && "border-danger-medium-red focus:border-danger-dark-red",
            )}
            error={errors ? errors[postalKey] : null}
            value={storeForm[postalKey] ?? ""}
            label={t("postal_code")}
            isDisabled={isDisabled}
            onBlur={validateForm}
          />
        </div>
      ) : (
        <div className="w-full">
          <DynamicLocationField
            labelClassName="text-neutral-grey-dark flex min-h-5 flex-row gap-2 font-medium"
            inputClassName={clsxm(
              "border-primary-light-solid text-neutral-black placeholder:text-neutral-grey-light focus:border-primary-medium-solid flex h-14 w-full rounded-md border bg-white p-4 outline-hidden transition-all duration-150",
              inputClassName,
              errors &&
                (errors[cityKey] || errors[streetKey] || errors[postalKey]) &&
                "border-danger-medium-red focus:border-danger-dark-red",
            )}
            defaultValue={storeForm[placeDescriptionKey] ?? ""}
            onBlur={() => {
              validateForm?.();
              onDynamicAddressBlur?.();
            }}
            onChange={debounce(() => {
              setSwitchButtonShown(true);
            }, 500)}
            onFocus={onDynamicAddressFocus}
            onPick={place => {
              storeSetter(
                streetKey,
                !place.street ? null : `${place.street}${place.streetNumber ? ` ${place.streetNumber}` : ""}`,
              );

              storeSetter(postalKey, place.postalCode);
              storeSetter(cityKey, place.city);
              storeSetter(countryKey, getCountryEnLabelByTranslatedLabel(place.country));
              storeSetter(placeIdKey, place.placeId);

              if (place.establishment) {
                storeSetter(
                  placeDescriptionKey,
                  `${place.establishment}${place.description ? ", " : ""}${place.description}`,
                );
              } else {
                storeSetter(placeDescriptionKey, place.description);
              }

              validateForm?.();
            }}
          />
          {errors && (errors[cityKey] || errors[streetKey] || errors[postalKey]) && (
            <ul className="text-danger-medium-red mt-0.5 p-0 text-xs">
              {errors[cityKey] && <li>{t(errors[cityKey])}</li>}
              {errors[streetKey] && <li>{t(errors[streetKey])}</li>}
              {errors[postalKey] && <li>{t(errors[postalKey])}</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(AddressForm);
